import { Link } from 'react-router-dom'
import PublicLayout from '../../components/layouts/PublicLayout'
import {
  Shield,
  Lock,
  Key,
  Eye,
  Server,
  RefreshCw,
  UserCheck,
  AlertTriangle,
  CheckCircle,
} from 'lucide-react'

const securityLayers = [
  {
    title: 'Encryption at Rest',
    description: 'All data encrypted using AES-256. Field-level encryption for high-risk PHI fields.',
    icon: Lock,
  },
  {
    title: 'Encryption in Transit',
    description: 'TLS 1.3 for all connections. HSTS enforced. No unencrypted traffic.',
    icon: Key,
  },
  {
    title: 'Access Control',
    description: 'Role-based access with minimum necessary principle. Per-client access assignments.',
    icon: UserCheck,
  },
  {
    title: 'Audit Logging',
    description: 'Immutable logs for all PHI access. Who, what, when, where - every action.',
    icon: Eye,
  },
  {
    title: 'Infrastructure',
    description: 'Azure US data centers. SOC 2 Type II certified. HIPAA BAA available.',
    icon: Server,
  },
  {
    title: 'Backup & Recovery',
    description: 'Automated backups with tested recovery. Documented RPO and RTO.',
    icon: RefreshCw,
  },
]

const accessControls = [
  {
    role: 'Admin',
    access: 'Full system access, user management, configuration',
  },
  {
    role: 'BCBA',
    access: 'Assigned clients, treatment plans, supervision, note attestation',
  },
  {
    role: 'RBT',
    access: 'Assigned clients only, sessions, notes (own)',
  },
  {
    role: 'Billing',
    access: 'Claims, payments, AR - no clinical notes content',
  },
  {
    role: 'Parent',
    access: 'Own child only, progress view, no clinical edit',
  },
]

export default function SecurityPage() {
  return (
    <PublicLayout>
      <div className="bg-gradient-to-b from-primary-50 to-white py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
              Security & HIPAA Compliance
            </h1>
            <p className="mt-6 text-lg text-gray-600">
              Built on Azure with enterprise-grade security. HIPAA compliant architecture
              with BAA available. Your PHI protected at every layer.
            </p>
          </div>
        </div>
      </div>

      {/* Security Layers */}
      <div className="bg-white py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {securityLayers.map((layer) => (
              <div
                key={layer.title}
                className="bg-gray-50 rounded-lg p-6 border border-gray-100"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-primary-100 rounded-lg">
                    <layer.icon className="h-6 w-6 text-primary-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">{layer.title}</h3>
                </div>
                <p className="text-sm text-gray-600">{layer.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Access Control */}
      <div className="bg-gray-900 py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-white">
              Role-Based Access Control
            </h2>
            <p className="mt-4 text-gray-300">
              Minimum necessary principle enforced at every level
            </p>
          </div>

          <div className="overflow-hidden rounded-lg border border-gray-700">
            <table className="min-w-full divide-y divide-gray-700">
              <thead className="bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Access Level
                  </th>
                </tr>
              </thead>
              <tbody className="bg-gray-800 divide-y divide-gray-700">
                {accessControls.map((control) => (
                  <tr key={control.role}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">
                      {control.role}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-300">
                      {control.access}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Session Security */}
      <div className="bg-white py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold tracking-tight text-gray-900">
                Session Security
              </h2>
              <p className="mt-4 text-gray-600">
                Multiple layers of session protection to prevent unauthorized access
              </p>

              <ul className="mt-8 space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">Automatic Timeout</p>
                    <p className="text-sm text-gray-600">Sessions expire after 1 hour of inactivity</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">Device Limits</p>
                    <p className="text-sm text-gray-600">Concurrent session limits per user</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">IP Monitoring</p>
                    <p className="text-sm text-gray-600">Anomalous access patterns detected</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">Token Refresh</p>
                    <p className="text-sm text-gray-600">Short-lived access tokens with rotation</p>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-4">Password Requirements</h3>
              <ul className="space-y-2">
                <li className="flex items-center gap-2 text-sm text-gray-600">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  Minimum 12 characters
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-600">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  Mix of upper/lower case
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-600">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  Numbers required
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-600">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  Common password check
                </li>
                <li className="flex items-center gap-2 text-sm text-gray-600">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  Account lockout after 5 failed attempts
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* BAA Section */}
      <div className="bg-primary-50 py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <Shield className="h-16 w-16 text-primary-600 mx-auto mb-6" />
            <h2 className="text-3xl font-bold tracking-tight text-gray-900">
              Business Associate Agreement
            </h2>
            <p className="mt-4 text-gray-600">
              We execute Business Associate Agreements (BAAs) with all customers.
              Our BAA covers the handling, storage, and processing of Protected
              Health Information in accordance with HIPAA requirements.
            </p>
            <div className="mt-8">
              <Link
                to="/contact"
                className="btn-primary text-base px-6 py-3"
              >
                Request BAA Information
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="bg-gray-900 py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-white">
            Security Questions?
          </h2>
          <p className="mt-4 text-lg text-gray-300">
            Our team can walk you through our security architecture and compliance posture.
          </p>
          <div className="mt-8">
            <Link to="/contact" className="btn bg-white text-gray-900 hover:bg-gray-100 text-base px-6 py-3">
              Contact Security Team
            </Link>
          </div>
        </div>
      </div>
    </PublicLayout>
  )
}
